import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const sortOptionsFromGetters = rootGetters => ({
  getInbox: id => rootGetters['inboxes/getInbox'](id),
});

// filter() returns a new array — safe to sort without mutating Vuex state
const filterAndSortConversations = (
  conversations,
  sortKey,
  predicate,
  rootGetters
) =>
  conversations
    .filter(predicate)
    .sort((a, b) =>
      sortComparator(a, b, sortKey, sortOptionsFromGetters(rootGetters))
    );

const getters = {
  getAllConversations: (
    { allConversations, chatSortFilter: sortKey },
    _getters,
    _rootState,
    rootGetters
  ) => {
    // Copy before sort — never mutate Vuex state inside a getter
    return [...allConversations].sort((a, b) =>
      sortComparator(a, b, sortKey, sortOptionsFromGetters(rootGetters))
    );
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    return selectedChat.attachments || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const { allConversations, chatSortFilter: sortKey } = _state;

    return filterAndSortConversations(
      allConversations,
      sortKey,
      conversation => {
        const { assignee } = conversation.meta;
        const isAssignedToMe = assignee && assignee.id === currentUserID;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isAssignedToMe && shouldFilter;
      },
      rootGetters
    );
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _getters, _rootState, rootGetters) => activeFilters => {
    const { allConversations, chatSortFilter: sortKey } = _state;

    return filterAndSortConversations(
      allConversations,
      sortKey,
      conversation => {
        const isUnAssigned = !conversation.meta.assignee;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isUnAssigned && shouldFilter;
      },
      rootGetters
    );
  },
  getAllStatusChats: (_state, _getters, _rootState, rootGetters) => activeFilters => {
    const { allConversations, chatSortFilter: sortKey } = _state;

    return filterAndSortConversations(
      allConversations,
      sortKey,
      conversation => applyPageFilters(conversation, activeFilters),
      rootGetters
    );
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },
};

export default getters;
